import { useEffect, useState } from "react";
import { discoverRelayers, type RelayerEndpoint } from "../lib/relayerRegistry";
import { RELAYER_REGISTRY_ADDRESS } from "../lib/env";

/**
 * RelayerRegistry browser. Lists every active relayer bonded in the
 * on-chain registry along with their advertised metadata. Falls back to
 * the configured single-relayer mode when no registry is set or the
 * registry is empty.
 */
export function Relayers() {
  const [endpoints, setEndpoints] = useState<RelayerEndpoint[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await discoverRelayers();
        if (alive) setEndpoints(list);
      } catch (err) {
        if (alive) setError((err as Error).message);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="flow flow--relayers">
      <h2 className="flow__title">Relayers</h2>
      <p className="lede">
        Relayers bond TONADO into the on-chain RelayerRegistry to be discoverable here. The dApp
        queries the registry directly; no centralized list. Relayers can be slashed by governance
        for misbehavior.
      </p>

      {!RELAYER_REGISTRY_ADDRESS && (
        <p className="lede lede--small">
          No RelayerRegistry configured (VITE_RELAYER_REGISTRY_ADDRESS). Showing the single
          configured fallback relayer.
        </p>
      )}

      {error && (
        <p className="error">
          <strong>Error:</strong> {error}
        </p>
      )}

      {!endpoints && !error && <p className="lede lede--small">Loading…</p>}

      {endpoints && endpoints.length > 0 && (
        <ul className="relayers-list">
          {endpoints.map((ep, i) => (
            <li key={i} className="summary summary--card">
              <div className="summary__row summary__row--accent">
                <dt>Endpoint</dt>
                <dd className="mono mono--break">{ep.metadata.endpoint}</dd>
              </div>
              {ep.metadata.name && (
                <div className="summary__row">
                  <dt>Name</dt>
                  <dd>{ep.metadata.name}</dd>
                </div>
              )}
              {ep.metadata.feeBps !== undefined && (
                <div className="summary__row">
                  <dt>Fee</dt>
                  <dd>{(ep.metadata.feeBps / 100).toFixed(2)}%</dd>
                </div>
              )}
              {ep.metadata.region && (
                <div className="summary__row">
                  <dt>Region</dt>
                  <dd>{ep.metadata.region}</dd>
                </div>
              )}
              {ep.metadata.description && (
                <div className="summary__row">
                  <dt>Description</dt>
                  <dd>{ep.metadata.description}</dd>
                </div>
              )}
              {ep.registered && (
                <div className="summary__row">
                  <dt>Bonded since</dt>
                  <dd>
                    {ep.registered.bondedAt
                      ? new Date(ep.registered.bondedAt * 1000).toISOString().slice(0, 19) + "Z"
                      : "-"}
                  </dd>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="lede lede--small">
        To register your own relayer, run <code>pnpm --filter @tonado/relayer register</code> or{" "}
        <code>tonado relayer register</code>.
      </p>
    </div>
  );
}
